import cn from 'classnames';
import { useEffect, useContext, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';

import VoucherDetailsModal from 'components/common/modals/VoucherDetailsModal';
import Subheading from 'components/common/Subheading';
import Text from 'components/common/Text';
import Loading from 'components/icons/Loading';
import CaretRight from 'components/icons/navigation/CaretRight';
import {
  UserContext,
  BasketContext,
  PickYourMealsContext,
  VoucherContext,
} from 'components/signUpFunnel/Contexts';
import LegalCopy from 'components/signUpFunnel/LegalCopy';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import RedesignedThankYouGreeting from 'components/signUpFunnel/thankYou/RedesignedThankYouGreeting';
import ThankYouCarousel from 'components/signUpFunnel/thankYou/ThankYouCarousel.js';
import ThankYouSubscriptionStatus from 'components/signUpFunnel/thankYou/ThankYouSubscriptionStatus';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import * as Routes from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import useFeatureFlag from 'hooks/useFeatureFlag';
import useFetchExperimentAssignment from 'hooks/useFetchExperimentAssignment';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { TEMPO, PAGE_VIEW_EVENT } from 'lib/constants';
import { THANK_YOU_PAGE_REDESIGN } from 'lib/constants/experimentConstants';
import { ALLOW_THANK_YOU_PAGE_DEVELOPMENT_WITHOUT_REDIRECTS } from 'lib/constants/featureFlags';
import * as ErrorLogging from 'services/ErrorLogging';
import * as Analytics from 'services/analytics';
import RequestService from 'services/RequestService';
import styles from 'styles/components/signUpFunnel/thankYou/ThankYouContainer';

import InviteFriendsInstructions from './InviteFriendsInstructions';
import NextSteps from './NextSteps';
import PickYourMealsHero from './PickYourMealsHero';
import ShareInvitations from './ShareInvitations';

const ThankYouContainer = () => {
  const { basket, setBasket } = useContext(BasketContext);
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);
  const { brand } = useContext(BrandContext);
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const { isUnderBp2: isMobileOrTablet } = useScreenSizeConstants();
  const mealPlanSlug = useFunnelMealPlan();
  const firstName = useMemo(() => user?.firstName, [user]);
  const isTempo = useBrand(TEMPO);
  const experimentAssignment = useFunnelExperimentAssignment();
  const { isVariant: isInRedesignedThankYouPageExperiment } =
    useFetchExperimentAssignment(THANK_YOU_PAGE_REDESIGN);
  const [isVoucherDetailsModalOpen, setIsVoucherDetailsModalOpen] = useState(false);
  const [basketVoucherDetails, setBasketVoucherDetails] = useState(null);
  const [offerRewards, setOfferRewards] = useState(null);
  const allowThankYouPageDevelopmentWithoutRedirects = useFeatureFlag(
    ALLOW_THANK_YOU_PAGE_DEVELOPMENT_WITHOUT_REDIRECTS
  );

  const weeklyBasket = offerRewards ? offerRewards.weeklyBasket : null;
  const upcomingOrderUrlPath = offerRewards
    ? Routes.dashboardMenuPath(offerRewards.currentMenuSlug)
    : null;

  const welcomeOfferMessage = voucher?.discounts?.length
    ? voucher.formattedDiscountString
      ? `${voucher.formattedDiscountString} Off`
      : voucher.welcomeMessage
    : null;
  const offerIsPresent = isInRedesignedThankYouPageExperiment && Boolean(welcomeOfferMessage);

  const { deliveryDate, scheduled, belowMinimum } = weeklyBasket || {};
  const renderVoucherDetailsModal =
    Boolean(basketVoucherDetails) && Boolean(weeklyBasket && Object.keys(weeklyBasket).length > 0);

  const openDisclaimerModal = useAnalyticsCallback(
    () => setIsVoucherDetailsModalOpen(true),
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].thankYou.welcomeOffersClicked);
      }
    },
    [brand]
  );

  useEffect(() => {
    if (!allowThankYouPageDevelopmentWithoutRedirects) {
      return;
    }

    if (basket) {
      const existingBasket = localStorage.getItem('firstOrderBasket');
      if (!existingBasket) {
        localStorage.setItem('firstOrderBasket', JSON.stringify(basket));
      }
    } else {
      const storedBasket = localStorage.getItem('firstOrderBasket');
      if (storedBasket) {
        const parsedBasket = JSON.parse(storedBasket);

        setBasket(parsedBasket);
        localStorage.removeItem('firstOrderBasket');
      }
    }
  }, [basket, setBasket, allowThankYouPageDevelopmentWithoutRedirects]);

  useEffect(() => {
    if (!isInRedesignedThankYouPageExperiment) return;

    const fetchVoucherDetails = async () => {
      try {
        const response = await RequestService.getVoucherDetails();

        if (response.ok && response.basketVoucherDetails?.orderDiscounts?.length) {
          setBasketVoucherDetails(response.basketVoucherDetails);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchVoucherDetails();
  }, [isInRedesignedThankYouPageExperiment]);

  useEffect(() => {
    if (!isInRedesignedThankYouPageExperiment) return;

    const fetchOfferRewards = async () => {
      try {
        const response = await RequestService.getOfferRewards();

        if (response.ok && response.offerRewards) {
          setOfferRewards(response.offerRewards);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchOfferRewards();
  }, [isInRedesignedThankYouPageExperiment]);

  const PickYourMealsGreeting = () => (
    <div className="flex flex-col items-center">
      <Text brandStylesOverrides="h1 text-tempo-black-100">Nicely done!</Text>
      <Text brandStylesOverrides="h1 text-tempo-black-100">Here’s what’s next:</Text>
    </div>
  );

  useMetadata({ title: `Step 6: Thank You | ${brandData(brand)?.displayName}` }, false);

  const onContinue = useAnalyticsCallback(
    null,
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug || TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug,
            ...experimentAssignment,
          });
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals, experimentAssignment]
  );

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.viewed, {
            mealPlanSlug: TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.viewed, experimentAssignment);
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals, experimentAssignment]
  );

  useEffect(() => {
    RequestService.v3.createEvents({ params: { eventName: PAGE_VIEW_EVENT } });
  }, []);

  useEffect(() => {
    if (location.state) setUser(user => ({ ...user, ...location.state.user }));
  }, [location.state]);

  const DefaultUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName={cn('px-4 pb-6 pt-12 bp1:px-0', {
          'bg-tempo-gray-100': isPickYourMeals,
        })}
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            <Skeleton count={2} className="px-4" />
          )
        }
      />
    );
  };

  const OfferDetailsSection = ({ className = '' }) => {
    if (!offerIsPresent) return null;

    return (
      <div className={className}>
        <Subheading brandStylesOverrides="h4" className="text-secondary mb-4 text-left">
          Your Welcome Offers
        </Subheading>
        <div
          role="button"
          tabIndex={0}
          aria-label="View promo details"
          className={cn(
            'flex h-[74px] w-full cursor-pointer items-center justify-between gap-4 rounded-2xl px-6 bp1:h-20',
            {
              'h6 bg-tempo-blue-50 text-tempo-black-100': isTempo,
              'bg-green-70 text-green-120': !isTempo,
            }
          )}
          onClick={openDisclaimerModal}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openDisclaimerModal();
            }
          }}
        >
          <div className="flex flex-1 flex-col items-start text-left">
            <Text brandStylesOverrides="h6">{welcomeOfferMessage}</Text>
            {voucher.discounts.length > 1 && (
              <Text brandStylesOverrides="textSm mt-1">
                Plus more savings over your first {voucher.discounts.length} orders
              </Text>
            )}
          </div>
          <CaretRight className="h-6 w-6 flex-shrink-0" />
        </div>
      </div>
    );
  };

  const InvitationSection = () => {
    if (isMobileOrTablet) {
      return (
        <section className="flex flex-col gap-10">
          <OfferDetailsSection className="w-full px-4" />
          <section>
            <ShareInvitations />
            <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
          </section>
        </section>
      );
    }

    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10">
        <OfferDetailsSection className="w-1/2" />
        <div className="flex flex-row">
          <div className="w-1/2">
            <ShareInvitations />
          </div>
          <div className="relative w-1/2">
            <div className="absolute left-0 top-4 h-3/4 border-l border-solid border-charcoal-60" />
            <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
          </div>
        </div>
      </div>
    );
  };

  const SmartphoneUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName="py-6"
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            "We're glad you've joined us!"
          )
        }
      />
    );
  };

  const SmartphoneInvitationSection = () => {
    return (
      <section className={cn(styles.smartphoneInvitationSection, 'relative flex flex-col')}>
        <ShareInvitations isSmartphoneScreenSize={isMobileOrTablet} />
        <InviteFriendsInstructions
          isSmartphoneScreenSize={isMobileOrTablet}
          isPickYourMeals={isPickYourMeals}
        />
      </section>
    );
  };

  const SmartphoneThankYouPage = () => {
    return (
      <section
        className={cn('px-6 bp2:hidden', isPickYourMeals ? 'bg-tempo-gray-40' : 'bg-transparent')}
      >
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : isInRedesignedThankYouPageExperiment ? (
          <>
            <RedesignedThankYouGreeting firstName={firstName} />
            <ThankYouCarousel isSmartphoneScreenSize={isMobileOrTablet} onContinue={onContinue} />
            <ThankYouSubscriptionStatus />
            <OfferDetailsSection className="w-full py-6" />
          </>
        ) : (
          <>
            <SmartphoneUserGreeting />
            <NextSteps onClick={onContinue} isSmartphoneScreenSize={isMobileOrTablet} />
            <OfferDetailsSection className="w-full py-6" />
          </>
        )}
        <SmartphoneInvitationSection />
        <LegalCopy onClick={onContinue} isSmartphoneScreenSize={isMobileOrTablet} />
      </section>
    );
  };

  const DefaultThankYouPage = () => {
    return (
      <section
        className={cn('hidden px-4 bp2:block bp2:px-8', { 'bg-tempo-gray-40': isPickYourMeals })}
      >
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : isInRedesignedThankYouPageExperiment ? (
          <>
            <RedesignedThankYouGreeting firstName={firstName} />
            <div className="mx-auto grid w-full max-w-6xl grid-cols-4 gap-8 self-stretch pb-8">
              <div className="col-span-3 w-full">
                <ThankYouCarousel onContinue={onContinue} />
              </div>
              <ThankYouSubscriptionStatus />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center px-20">
            <div
              className={cn(
                'my-6 flex w-full max-w-5xl flex-col rounded-2xl border border-solid bp1:px-6',
                isTempo ? 'border-charcoal-80 bg-white-100' : 'border-cream-120 bg-cream-110'
              )}
            >
              <DefaultUserGreeting />
              <NextSteps onClick={onContinue} />
            </div>
          </div>
        )}
        <InvitationSection />
        <LegalCopy onClick={onContinue} />
      </section>
    );
  };

  return user ? (
    <>
      {isMobileOrTablet ? <SmartphoneThankYouPage /> : <DefaultThankYouPage />}
      {renderVoucherDetailsModal && (
        <VoucherDetailsModal
          basketVoucherDetails={basketVoucherDetails}
          deliveryDate={deliveryDate}
          displayAppliedTag={scheduled && !belowMinimum}
          shown={isVoucherDetailsModalOpen}
          upcomingOrderUrlPath={upcomingOrderUrlPath}
          viewOrderAnalyticsEvent={
            Analytics.events.signUp[brand]?.thankYou?.upcomingOrdersOffersClicked
          }
          onCloseVoucherDetailsModal={() => setIsVoucherDetailsModalOpen(false)}
        />
      )}
    </>
  ) : (
    <Loading />
  );
};

export default ThankYouContainer;
